import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { usePageContext } from 'vike-react/usePageContext'
import { langCodeToName } from '../../utils/appTheme.js'
import { recordAttempt, readProgress } from '../../utils/progress.js'
import { loadVerbData, generateDrillCards, getVerbType, type DrillCardData, type ConjTense } from '../../utils/conjugation.js'
import { MotifBand, motifLabel } from '../../components/Motifs.js'
import { sound } from '../../utils/garden-sound.js'
import { FlashcardGame as SharedFlashcardGame } from '../../components/FlashcardGame.js'

const SESSION_STORE_KEY = 'c8-sessions'
type SessionEntry = { verb: string; tense: string; correct: number; total: number; timestamp: number }

function saveSession(entry: SessionEntry) {
  if (typeof window === 'undefined') return
  try {
    const raw = localStorage.getItem(SESSION_STORE_KEY)
    const sessions: SessionEntry[] = raw ? JSON.parse(raw) : []
    sessions.unshift(entry)
    if (sessions.length > 20) sessions.length = 20
    localStorage.setItem(SESSION_STORE_KEY, JSON.stringify(sessions))
  } catch {}
}

// ── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', surf2: 'var(--surf2)', surf3: 'var(--surf3)',
  border: 'var(--border)', border2: 'var(--border2)',
  text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
  accent: 'var(--accent)', accentBg: 'var(--accent-bg)', accentBorder: 'var(--accent-border)',
  green: 'var(--success)', greenBg: 'var(--success-bg)', greenBorder: 'var(--success-border)',
  red: 'var(--error)', redBg: 'var(--error-bg)', redBorder: 'var(--error-border)',
  amber: 'var(--warn)', amberBg: 'var(--warn-bg)', amberBorder: 'var(--warn-border)',
  blue: 'var(--blue)', blueBg: 'var(--blue-bg)',
}
const AF = {
  h: 'var(--font-h, "Space Grotesk", system-ui, sans-serif)',
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
}

// ── useIsMobile hook (SSR-safe) ──────────────────────────────────────────────
function useIsMobile() {
  const [mobile, setMobile] = useState(false)
  useEffect(() => {
    const check = () => setMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  return mobile
}

// ── Data ─────────────────────────────────────────────────────────────────────
const PRONOUNS_ES = ['yo', 'tú', 'él / ella', 'nosotros', 'vosotros', 'ellos']
const PRONOUNS_PT = ['eu', 'você', 'ele/ela', 'nós', 'vocês', 'eles/elas']
const PRONOUNS_FI = ['minä', 'sinä', 'hän', 'me', 'te', 'he']

const TENSE_OPTIONS: { id: ConjTense; label: string; sublabel: string }[] = [
  { id: 'present',              label: 'Present',          sublabel: 'Indicativo'    },
  { id: 'preterite',            label: 'Preterite',        sublabel: 'Indefinido'    },
  { id: 'imperfect',            label: 'Imperfect',        sublabel: 'Imperfecto'    },
  { id: 'future',               label: 'Future',           sublabel: 'Futuro'        },
  { id: 'conditional',          label: 'Conditional',      sublabel: 'Condicional'   },
  { id: 'present_subjunctive',  label: 'Pres. Subj.',      sublabel: 'Subjuntivo'    },
  { id: 'imperfect_subjunctive',label: 'Imp. Subj.',       sublabel: 'Imp. Subj.'    },
  { id: 'present_perfect',      label: 'Pres. Perfect',    sublabel: 'Perfecto'      },
  { id: 'pluperfect',           label: 'Pluperfect',       sublabel: 'Pluscuamperf.' },
]

const TENSE_OPTIONS_FI: { id: ConjTense; label: string; sublabel: string }[] = [
  { id: 'present',              label: 'Present',          sublabel: 'Preesens'      },
  { id: 'conditional',          label: 'Conditional',      sublabel: 'Konditionaali' },
  { id: 'present_perfect',      label: 'Pres. Perfect',    sublabel: 'Perfekti'      },
]
const VERB_TYPES = ['All types', 'Regular', 'Irregular']
const SESSION_LENGTHS: (number | 'All')[] = [5, 10, 20, 'All']


const FLASH_CARDS_ES = [
  { id: 'f1', front: 'yo hablo',          back: 'I speak / I am speaking',              verb: 'hablar', note: 'Present Indicative' },
  { id: 'f2', front: 'nosotros hablamos', back: 'we speak / we are speaking',            verb: 'hablar', note: 'Present Indicative' },
  { id: 'f3', front: 'él habló',          back: 'he spoke (completed, specific moment)', verb: 'hablar', note: 'Preterite'          },
  { id: 'f4', front: 'yo hablaba',        back: 'I used to speak / I was speaking',      verb: 'hablar', note: 'Imperfect'          },
  { id: 'f5', front: 'yo soy',            back: 'I am (permanent identity)',             verb: 'ser',    note: 'Present Indicative' },
  { id: 'f6', front: 'tú tienes',         back: 'you have',                              verb: 'tener',  note: 'Present Indicative' },
]

const SENTENCE_CARDS = [
  { id: 1, prompt: 'She used to speak to him every day.', tiles: ['ella', 'le', 'hablaba', 'todos los días', 'a él'],  answer: ['ella', 'le', 'hablaba', 'a él', 'todos los días'], correct: 'Ella le hablaba a él todos los días.' },
  { id: 2, prompt: 'We are going to the store tomorrow.', tiles: ['nosotros', 'vamos', 'mañana', 'a la tienda'],        answer: ['nosotros', 'vamos', 'a la tienda', 'mañana'],      correct: 'Nosotros vamos a la tienda mañana.' },
  { id: 3, prompt: 'Do you have a pen?',                  tiles: ['¿', 'tienes', 'tú', 'un bolígrafo', '?'],           answer: ['¿', 'tienes', 'tú', 'un bolígrafo', '?'],          correct: '¿Tienes tú un bolígrafo?' },
]

const PREBUILT_DECKS_ES = [
  {
    id: 'es-present-core', name: 'Present Core', emoji: '🌱', lang: 'Spanish',
    desc: 'The most essential present tense forms for everyday speech.',
    cards: [
      { id: 'p1',  front: 'yo hablo',          back: 'I speak / I am speaking',       verb: 'hablar', note: 'Present · hablar' },
      { id: 'p2',  front: 'tú hablas',         back: 'you speak',                     verb: 'hablar', note: 'Present · hablar' },
      { id: 'p3',  front: 'nosotros hablamos', back: 'we speak',                      verb: 'hablar', note: 'Present · hablar' },
      { id: 'p4',  front: 'ellos hablan',      back: 'they speak',                    verb: 'hablar', note: 'Present · hablar' },
      { id: 'p5',  front: 'yo soy',            back: 'I am (permanent identity)',     verb: 'ser',    note: 'Present · ser'    },
      { id: 'p6',  front: 'tú eres',           back: 'you are',                       verb: 'ser',    note: 'Present · ser'    },
      { id: 'p7',  front: 'él es',             back: 'he/she is',                     verb: 'ser',    note: 'Present · ser'    },
      { id: 'p8',  front: 'yo tengo',          back: 'I have',                        verb: 'tener',  note: 'Present · tener'  },
      { id: 'p9',  front: 'tú tienes',         back: 'you have',                      verb: 'tener',  note: 'Present · tener'  },
      { id: 'p10', front: 'yo estoy',          back: 'I am (state / location)',       verb: 'estar',  note: 'Present · estar'  },
    ],
  },
  {
    id: 'es-ser-estar', name: 'Ser vs Estar', emoji: '⚖️', lang: 'Spanish',
    desc: 'The two verbs for "to be" — one of the most important distinctions in Spanish.',
    cards: [
      { id: 'se1', front: 'soy estudiante',    back: 'I am a student (permanent role)',     verb: 'ser',   note: 'Ser — identity'   },
      { id: 'se2', front: 'estoy cansado',      back: 'I am tired (temporary state)',       verb: 'estar', note: 'Estar — state'    },
      { id: 'se3', front: 'es de México',       back: 'he is from Mexico (origin)',         verb: 'ser',   note: 'Ser — origin'     },
      { id: 'se4', front: 'está en casa',       back: 'he/she is at home (location)',       verb: 'estar', note: 'Estar — location' },
      { id: 'se5', front: 'somos amigos',       back: 'we are friends (permanent)',         verb: 'ser',   note: 'Ser — relation'   },
      { id: 'se6', front: 'estamos contentos',  back: 'we are happy (right now)',           verb: 'estar', note: 'Estar — emotion'  },
      { id: 'se7', front: '¿cómo estás?',       back: 'how are you? (how are you feeling?)', verb: 'estar', note: 'Estar — feeling' },
      { id: 'se8', front: '¿cómo eres?',        back: 'what are you like? (personality)',  verb: 'ser',   note: 'Ser — character'  },
    ],
  },
  {
    id: 'es-preterite', name: 'Preterite', emoji: '⏪', lang: 'Spanish',
    desc: 'Completed past actions at a specific moment.',
    cards: [
      { id: 'pr1', front: 'yo hablé',          back: 'I spoke (completed)',               verb: 'hablar', note: 'Preterite · hablar' },
      { id: 'pr2', front: 'tú hablaste',       back: 'you spoke',                         verb: 'hablar', note: 'Preterite · hablar' },
      { id: 'pr3', front: 'él habló',          back: 'he/she spoke',                      verb: 'hablar', note: 'Preterite · hablar' },
      { id: 'pr4', front: 'nosotros hablamos', back: 'we spoke (same as present!)',       verb: 'hablar', note: 'Preterite · hablar' },
      { id: 'pr5', front: 'yo fui',            back: 'I went / I was',                    verb: 'ir/ser', note: 'Preterite · ir/ser' },
      { id: 'pr6', front: 'yo tuve',           back: 'I had',                             verb: 'tener',  note: 'Preterite · tener'  },
      { id: 'pr7', front: 'yo hice',           back: 'I did / I made',                    verb: 'hacer',  note: 'Preterite · hacer'  },
    ],
  },
  {
    id: 'es-phrases', name: 'Key Phrases', emoji: '💬', lang: 'Spanish',
    desc: 'High-frequency conjugated phrases you need from day one.',
    cards: [
      { id: 'kp1', front: 'tengo que ir',     back: 'I have to go',              verb: 'tener',  note: 'tener que + inf.' },
      { id: 'kp2', front: 'voy a hablar',     back: 'I am going to speak',       verb: 'ir',     note: 'ir a + inf.'      },
      { id: 'kp3', front: 'quiero aprender',  back: 'I want to learn',           verb: 'querer', note: 'querer + inf.'    },
      { id: 'kp4', front: 'puedo ayudar',     back: 'I can help',                verb: 'poder',  note: 'Present · poder'  },
      { id: 'kp5', front: 'sé hablar',        back: 'I know how to speak',       verb: 'saber',  note: 'saber + inf.'     },
      { id: 'kp6', front: 'hay que practicar',back: 'one must practice',         verb: 'haber',  note: 'hay que + inf.'   },
    ],
  },
]

const PREBUILT_DECKS_PT = [
  {
    id: 'pt-present-core', name: 'Present Core', emoji: '🌱', lang: 'Portuguese',
    desc: 'Essential present tense forms in Brazilian Portuguese.',
    cards: [
      { id: 'pp1',  front: 'eu falo',       back: 'I speak / I am speaking',          verb: 'falar', note: 'Presente · falar' },
      { id: 'pp2',  front: 'você fala',     back: 'you speak',                        verb: 'falar', note: 'Presente · falar' },
      { id: 'pp3',  front: 'nós falamos',   back: 'we speak',                         verb: 'falar', note: 'Presente · falar' },
      { id: 'pp4',  front: 'vocês falam',   back: 'you all speak',                    verb: 'falar', note: 'Presente · falar' },
      { id: 'pp5',  front: 'eu sou',        back: 'I am (permanent)',                 verb: 'ser',   note: 'Presente · ser'   },
      { id: 'pp6',  front: 'você é',        back: 'you are',                          verb: 'ser',   note: 'Presente · ser'   },
      { id: 'pp7',  front: 'eu tenho',      back: 'I have',                           verb: 'ter',   note: 'Presente · ter'   },
      { id: 'pp8',  front: 'eu estou',      back: 'I am (temporary)',                 verb: 'estar', note: 'Presente · estar' },
      { id: 'pp9',  front: 'tudo bem?',     back: 'all good? (common greeting)',      verb: '—',     note: 'Phrase'           },
      { id: 'pp10', front: 'tenho saudade', back: 'I miss (someone/something)',       verb: 'ter',   note: 'Saudade'          },
    ],
  },
  {
    id: 'pt-ser-estar', name: 'Ser vs Estar', emoji: '⚖️', lang: 'Portuguese',
    desc: 'Permanent vs temporary states — essential distinction in Portuguese.',
    cards: [
      { id: 'ps1', front: 'sou brasileiro', back: 'I am Brazilian (permanent)',         verb: 'ser',   note: 'Ser — identity'   },
      { id: 'ps2', front: 'estou bem',      back: 'I am well (right now)',              verb: 'estar', note: 'Estar — state'    },
      { id: 'ps3', front: 'é de São Paulo', back: 'he/she is from São Paulo',          verb: 'ser',   note: 'Ser — origin'     },
      { id: 'ps4', front: 'está em casa',   back: 'he/she is at home (location)',      verb: 'estar', note: 'Estar — location' },
      { id: 'ps5', front: 'somos amigos',   back: 'we are friends',                    verb: 'ser',   note: 'Ser — relation'   },
    ],
  },
]

// ── Type defs ────────────────────────────────────────────────────────────────
type DrillCard = DrillCardData
type FlashCard = { id: string; front: string; back: string; verb: string; note: string }
type SentenceCard = typeof SENTENCE_CARDS[number]

// ── Shared small components ──────────────────────────────────────────────────
function Kbd({ children }: { children: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: AF.m, fontSize: 10, color: C.muted,
      background: C.surf2, border: `1px solid ${C.border}`,
      borderBottomWidth: 2, padding: '1px 5px', borderRadius: 4,
      lineHeight: 1.5, userSelect: 'none', whiteSpace: 'nowrap', flexShrink: 0,
    }}>{children}</span>
  )
}

function CounterBadge({ current, total }: { current: number; total: number }) {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <span style={{ fontFamily: AF.m, fontSize: 13, color: C.text, fontWeight: 600 }}>{current}</span>
      <span style={{ fontFamily: AF.m, fontSize: 11, color: C.dim }}>/ {total}</span>
    </div>
  )
}

function ModeInstruction({ icon, text }: { icon: string; text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 24 }}>
      <div style={{ width: 28, height: 28, borderRadius: 7, background: C.accentBg, border: `1px solid ${C.accentBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
        {icon}
      </div>
      <span style={{ fontFamily: AF.b, fontSize: 13, color: C.muted }}>{text}</span>
    </div>
  )
}

function KbdRow({ hints }: { hints: [string, string][] }) {
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', padding: '10px 24px 16px', flexShrink: 0 }}>
      {hints.map(([key, label]) => (
        <span key={key + label} style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <Kbd>{key}</Kbd>
          <span style={{ fontFamily: AF.b, fontSize: 11, color: C.dim }}>{label}</span>
        </span>
      ))}
    </div>
  )
}

function ScoreScreen({ score, total, onRetry }: { score: number; total: number; onRetry: () => void }) {
  const pct = total > 0 ? Math.round((score / total) * 100) : 100
  useEffect(() => { pct >= 80 ? sound.victory() : sound.wither() }, [])
  const color = pct >= 80 ? 'var(--success)' : pct >= 50 ? 'var(--warn)' : 'var(--error)'
  const R = 48
  const C2PI = 2 * Math.PI * R
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ textAlign: 'center', maxWidth: 380, padding: '0 24px' }}>
        {total > 0 ? (
          <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto 28px' }}>
            <svg width="120" height="120" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r={R} fill="none" stroke={C.surf2} strokeWidth="7"/>
              <circle cx="60" cy="60" r={R} fill="none" stroke={color} strokeWidth="7"
                strokeDasharray={C2PI}
                strokeDashoffset={C2PI * (1 - pct / 100)}
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
                style={{ transition: 'stroke-dashoffset 1s ease 0.2s' }}
              />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontFamily: AF.h, fontSize: 26, fontWeight: 700, color, letterSpacing: '-1px', lineHeight: 1 }}>{pct}%</span>
              <span style={{ fontFamily: AF.m, fontSize: 11, color: C.muted }}>{score}/{total}</span>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 48, marginBottom: 20 }}>✓</div>
        )}
        <div style={{ fontFamily: AF.h, fontSize: 26, fontWeight: 700, color: C.text, letterSpacing: '-0.8px', marginBottom: 10 }}>
          {total === 0 ? 'Set complete.' : pct === 100 ? 'Perfect.' : pct >= 80 ? 'Well done.' : pct >= 50 ? 'Good effort.' : 'Keep at it.'}
        </div>
        <div style={{ fontFamily: AF.b, fontSize: 15, color: C.muted, marginBottom: 36, lineHeight: 1.6 }}>
          {total > 0 ? `${score} of ${total} correct.` : `You reviewed all ${score} cards.`}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={onRetry} style={{ fontFamily: AF.b, fontSize: 14, fontWeight: 700, background: 'var(--accent)', color: C.bg, border: 'none', padding: '12px 28px', borderRadius: 8, cursor: 'pointer' }}>
            Try again
          </button>
          <button onClick={onRetry} style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, background: 'transparent', border: `1px solid ${C.border}`, padding: '12px 24px', borderRadius: 8, cursor: 'pointer' }}>
            Next set →
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Chip button ──────────────────────────────────────────────────────────────
function Chip({ label, active, onClick, disabled, sub }: { label: string | number; active: boolean; onClick?: () => void; disabled?: boolean; sub?: string }) {
  return (
    <button onClick={disabled ? undefined : onClick} style={{
      fontFamily: AF.b, fontSize: 13, fontWeight: active ? 700 : 400,
      padding: '8px 16px', borderRadius: 20,
      border: `1px solid ${active ? 'var(--accent)' : C.border}`,
      background: active ? 'var(--accent)' : disabled ? 'transparent' : C.surf,
      color: active ? C.bg : disabled ? C.dim : C.text,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
      display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
    }}>
      {String(label)}
      {disabled && <span style={{ fontFamily: AF.m, fontSize: 8, color: C.dim, letterSpacing: '0.06em' }}>SOON</span>}
      {sub && !disabled && <span style={{ fontFamily: AF.m, fontSize: 10, color: active ? C.bg : C.dim }}>{sub}</span>}
    </button>
  )
}

// ── 1. DRILLS ────────────────────────────────────────────────────────────────
function DrillSetup({ langCode, verbData, loading, onStart }: {
  langCode: string
  verbData: Record<string, any>
  loading: boolean
  onStart: (cards: DrillCard[]) => void
}) {
  const isMobile = useIsMobile()
  const hp = isMobile ? '20px' : '48px'
  const pronounList = langCode === 'pt' ? PRONOUNS_PT : langCode === 'fi' ? PRONOUNS_FI : PRONOUNS_ES

  const [tenses, setTenses]     = useState<Set<ConjTense>>(() => new Set(['present'] as ConjTense[]))
  const [verbType, setVerbType] = useState('All types')
  const [length, setLength]     = useState<number | 'All'>(10)
  const [pronouns, setPronouns] = useState<Set<string>>(() => new Set(pronounList))

  useEffect(() => { setPronouns(new Set(langCode === 'pt' ? PRONOUNS_PT : langCode === 'fi' ? PRONOUNS_FI : PRONOUNS_ES)) }, [langCode])

  const togglePronoun = (p: string) => setPronouns(prev => {
    const next = new Set(prev)
    if (next.has(p) && next.size > 1) next.delete(p); else next.add(p)
    return next
  })

  const toggleTense = (t: ConjTense) => setTenses(prev => {
    const next = new Set(prev)
    if (next.has(t) && next.size > 1) next.delete(t); else next.add(t)
    return next
  })

  const verbTypeEnum = verbType === 'Irregular' ? 'irregular' : verbType === 'Regular' ? 'regular' : 'all'

  const estimatedCount = useMemo(() => {
    if (loading || Object.keys(verbData).length === 0) return 0
    const allVerbs = Object.keys(verbData).filter(v =>
      langCode === 'fi' ? true : (!v.endsWith('se') && ['ar','er','ir'].includes(v.slice(-2)))
    )
    const matchVerbs = allVerbs.filter(v => {
      const vt = getVerbType(v, verbData)
      if (verbTypeEnum === 'regular' && vt === 'Irregular') return false
      if (verbTypeEnum === 'irregular' && vt !== 'Irregular') return false
      return true
    })
    const total = matchVerbs.length * pronouns.size * tenses.size
    return length === 'All' ? total : Math.min(Number(length), total)
  }, [verbData, verbTypeEnum, pronouns, tenses, length, loading])

  const handleStart = () => {
    if (estimatedCount === 0 || loading) return
    const count = length === 'All' ? 9999 : Number(length)
    // Prioritise verbs the user has already encountered in vocabulary practice
    const store = readProgress()
    const priorityVerbs = Object.entries(store.vocabularyByTerm)
      .filter(([, s]) => s.attempts >= 1)
      .map(([term]) => term)
    const cards = generateDrillCards({ data: verbData, langCode, pronouns: [...pronouns], tenses: [...tenses], verbType: verbTypeEnum as 'all' | 'regular' | 'irregular', count, priorityVerbs })
    if (cards.length > 0) onStart(cards)
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg, padding: `28px ${hp}` }}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        <h2 style={{ fontFamily: AF.h, fontSize: 22, fontWeight: 700, color: C.text, margin: '0 0 4px', letterSpacing: '-0.7px' }}>Configure session</h2>
        <p style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, margin: '0 0 28px' }}>Choose what to drill, then start when ready.</p>

        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em' }}>TENSES</div>
            <button onClick={() => { const opts = langCode === 'fi' ? TENSE_OPTIONS_FI : TENSE_OPTIONS; setTenses(tenses.size === opts.length ? new Set([opts[0].id]) : new Set(opts.map((t: any) => t.id))) }} style={{ fontFamily: AF.b, fontSize: 11, color: 'var(--accent)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
              {tenses.size === (langCode === 'fi' ? TENSE_OPTIONS_FI : TENSE_OPTIONS).length ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {(langCode === 'fi' ? TENSE_OPTIONS_FI : TENSE_OPTIONS).map(t => (
              <button key={t.id} onClick={() => toggleTense(t.id)} style={{
                fontFamily: AF.b, fontSize: 13, fontWeight: 500,
                padding: '9px 16px', borderRadius: 8,
                border: `1px solid ${tenses.has(t.id) ? 'var(--accent)' : C.border}`,
                background: tenses.has(t.id) ? C.accentBg : 'transparent',
                color: tenses.has(t.id) ? 'var(--accent)' : C.muted, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{ fontSize: 10, opacity: tenses.has(t.id) ? 1 : 0, width: 9, flexShrink: 0 }}>✓</span>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ height: 1, background: C.border, margin: '0 0 24px' }}/>

        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em' }}>PRONOUNS TO INCLUDE</div>
            <button onClick={() => setPronouns(pronouns.size === pronounList.length ? new Set([pronounList[0]]) : new Set(pronounList))} style={{ fontFamily: AF.b, fontSize: 11, color: 'var(--accent)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
              {pronouns.size === pronounList.length ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {pronounList.map(p => (
              <button key={p} onClick={() => togglePronoun(p)} style={{
                fontFamily: AF.m, fontSize: 13, fontWeight: 500,
                padding: '9px 16px', borderRadius: 8,
                border: `1px solid ${pronouns.has(p) ? 'var(--accent)' : C.border}`,
                background: pronouns.has(p) ? C.accentBg : 'transparent',
                color: pronouns.has(p) ? 'var(--accent)' : C.muted, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{ fontSize: 10, opacity: pronouns.has(p) ? 1 : 0, width: 9, flexShrink: 0 }}>✓</span>
                {p}
              </button>
            ))}
          </div>
        </div>
        <div style={{ height: 1, background: C.border, margin: '0 0 24px' }}/>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 10 }}>VERB TYPE</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {VERB_TYPES.map(vt => <Chip key={vt} label={vt} active={verbType === vt} onClick={() => setVerbType(vt)}/>)}
          </div>
        </div>
        <div style={{ height: 1, background: C.border, margin: '0 0 24px' }}/>

        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 10 }}>SESSION LENGTH</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {SESSION_LENGTHS.map(l => <Chip key={String(l)} label={l === 'All' ? 'All' : `${l} cards`} active={length === l} onClick={() => setLength(l)}/>)}
          </div>
        </div>

        <div style={{ padding: '14px 18px', borderRadius: 9, background: C.surf, border: `1px solid ${C.border}`, marginBottom: 22, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontFamily: AF.h, fontSize: 18, fontWeight: 700, color: loading ? C.dim : estimatedCount > 0 ? 'var(--accent)' : C.dim, letterSpacing: '-0.5px' }}>
              {loading ? '…' : `~${estimatedCount} card${estimatedCount !== 1 ? 's' : ''}`}
            </div>
            <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, marginTop: 2 }}>
              {pronouns.size} pronoun{pronouns.size !== 1 ? 's' : ''} · {tenses.size} tense{tenses.size !== 1 ? 's' : ''} · {loading ? 'Loading verb dataset…' : 'Full verb dataset'}
            </div>
          </div>
          {!loading && estimatedCount === 0 && <span style={{ fontFamily: AF.b, fontSize: 12, color: 'var(--error)' }}>No cards match — adjust your filters</span>}
        </div>

        <button onClick={handleStart} disabled={estimatedCount === 0 || loading} style={{
          fontFamily: AF.b, fontSize: 15, fontWeight: 700,
          background: estimatedCount > 0 && !loading ? 'var(--accent)' : C.surf2,
          color: estimatedCount > 0 && !loading ? C.bg : C.dim,
          border: 'none', padding: '14px 32px', borderRadius: 9, cursor: estimatedCount > 0 && !loading ? 'pointer' : 'not-allowed',
        }}>
          {loading ? 'Loading…' : estimatedCount > 0 ? `Start ${estimatedCount} cards →` : 'Start —'}
        </button>
      </div>
    </div>
  )
}

function DrillGame({ cards, isMobile, onReset }: { cards: DrillCard[]; isMobile: boolean; onReset: () => void }) {
  const [idx, setIdx]       = useState(0)
  const [input, setInput]   = useState('')
  const [state, setState]   = useState<'idle' | 'correct' | 'wrong'>('idle')
  const [score, setScore]   = useState(0)
  const [done, setDone]     = useState(false)
  const [shaking, setShake] = useState(false)
  const inputRef            = useRef<HTMLInputElement>(null)
  const card                = cards[idx]
  const norm                = (s: string) => s.trim().toLowerCase().normalize('NFC')

  const check = useCallback(() => {
    if (!input.trim()) return
    const isRight = norm(input) === norm(card.answer)
    recordAttempt({ timestamp: Date.now(), infinitive: card.verb, tense: card.tense, pronoun: card.pronoun, status: isRight ? 'correct' : 'incorrect' })
    if (isRight) {
      sound.correct(score); setState('correct'); setScore(s => s + 1)
    } else {
      sound.miss(); setState('wrong')
      setShake(true); setTimeout(() => setShake(false), 400)
    }
  }, [input, card])

  const advance = useCallback(() => {
    if (idx + 1 >= cards.length) {
      saveSession({ verb: cards[0].verb, tense: cards[0].tenseLabel, correct: score, total: cards.length, timestamp: Date.now() })
      setDone(true); return
    }
    setIdx(i => i + 1); setInput(''); setState('idle')
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [idx, cards.length, score])

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return
      if (state !== 'idle' && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); advance() }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [state, advance])

  useEffect(() => { inputRef.current?.focus() }, [idx])

  if (done) return (
    <ScoreScreen score={score} total={cards.length}
      onRetry={() => { setIdx(0); setInput(''); setState('idle'); setScore(0); setDone(false) }}/>
  )

  const hp = isMobile ? '20px' : '64px'
  const isCorrect = state === 'correct'
  const isWrong   = state === 'wrong'

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: isCorrect ? C.greenBg : isWrong ? C.redBg : C.bg, transition: 'background 0.28s', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: `12px ${hp} 0` }}>
        <div style={{ flex: 1, height: 3, background: C.border, borderRadius: 2 }}>
          <div style={{ height: '100%', background: 'var(--accent)', width: `${(idx / cards.length) * 100}%`, transition: 'width 0.4s', borderRadius: 2 }}/>
        </div>
        <CounterBadge current={idx + 1} total={cards.length}/>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: `24px ${hp}`, maxWidth: 620, width: '100%', alignSelf: 'center' }}>
        <ModeInstruction icon="✏️" text="Type the correct conjugation for this pronoun"/>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: AF.h, fontSize: isMobile ? 52 : 80, fontWeight: 700, color: C.text, letterSpacing: '-3px', lineHeight: 1, marginBottom: 8 }}>
            {card.verb}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: AF.b, fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>{card.tenseLabel}</span>
            <span style={{ color: C.border }}>·</span>
            <span style={{ fontFamily: AF.m, fontSize: 12, color: C.muted }}>{card.verbType}</span>
          </div>
        </div>

        <div className={shaking ? 'c8-shake' : ''} style={{
          display: 'flex', alignItems: 'stretch', gap: 0,
          borderRadius: 12, overflow: 'hidden',
          border: `2px solid ${isCorrect ? C.greenBorder : isWrong ? C.redBorder : C.border2}`,
          background: C.surf, marginBottom: 20, transition: 'border-color 0.2s',
        }}>
          <div style={{
            padding: isMobile ? '18px 16px' : '22px 24px',
            background: C.surf2, borderRight: `1px solid ${C.border}`,
            fontFamily: AF.m, fontSize: isMobile ? 22 : 30, fontWeight: 500,
            color: C.text, letterSpacing: '-0.5px', whiteSpace: 'nowrap', flexShrink: 0,
            minWidth: isMobile ? 110 : 150,
            display: 'flex', alignItems: 'center',
          }}>
            {card.pronoun}
          </div>
          {/* Fixed-height panel — overlay idle/answer states so box never shifts */}
          <div style={{ flex: 1, position: 'relative', minHeight: isMobile ? 90 : 106 }}>
            {/* Idle: input */}
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center',
              padding: isMobile ? '0 16px' : '0 24px',
              opacity: state === 'idle' ? 1 : 0,
              pointerEvents: state === 'idle' ? 'auto' : 'none',
            }}>
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); if (state === 'idle') check(); else advance() }
                  if (e.key === 'Tab') { e.preventDefault(); advance() }
                }}
                placeholder="conjugation…"
                style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontFamily: AF.m, fontSize: isMobile ? 22 : 30, color: C.text, letterSpacing: '-0.5px' }}
                autoComplete="off" spellCheck={false}
              />
            </div>
            {/* Answer state — overlaid at same position */}
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column', justifyContent: 'center',
              padding: isMobile ? '12px 16px' : '14px 24px',
              opacity: state !== 'idle' ? 1 : 0,
              transition: 'opacity 0.12s',
              pointerEvents: state !== 'idle' ? 'auto' : 'none',
            }}>
              {isWrong && (
                <div style={{ fontFamily: AF.m, fontSize: isMobile ? 14 : 18, color: 'var(--error)', textDecoration: 'line-through', opacity: 0.65, marginBottom: 4 }}>{input}</div>
              )}
              <div style={{ fontFamily: AF.m, fontSize: isMobile ? 22 : 30, fontWeight: 500, color: isCorrect ? 'var(--success)' : C.text, letterSpacing: '-0.5px' }}>{card.answer}</div>
              <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, marginTop: 4 }}>{card.translation}</div>
            </div>
          </div>
        </div>

        {state === 'idle' ? (
          <button onClick={check} style={{
            alignSelf: 'flex-start', fontFamily: AF.b, fontSize: 14, fontWeight: 700,
            background: input.trim() ? 'var(--accent)' : C.surf2,
            color: input.trim() ? C.bg : C.dim,
            border: 'none', padding: '13px 26px', borderRadius: 9, cursor: input.trim() ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', gap: 9,
          }}>
            Check answer <Kbd>↵</Kbd>
          </button>
        ) : (
          <button onClick={advance} className="c8-pop" style={{
            alignSelf: 'flex-start', fontFamily: AF.b, fontSize: 14, fontWeight: 700,
            background: isCorrect ? 'var(--success)' : C.surf,
            color: isCorrect ? C.bg : C.text,
            border: `1px solid ${isCorrect ? C.greenBorder : C.border}`,
            padding: '13px 26px', borderRadius: 9, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 9,
          }}>
            {isCorrect ? 'Next →' : 'Continue →'} <Kbd>↵</Kbd>
          </button>
        )}
      </div>

      <KbdRow hints={state === 'idle' ? [['↵', 'check'], ['Tab', 'skip']] : [['↵', 'next card']]}/>
    </div>
  )
}

function DrillsSection({ langCode }: { langCode: string }) {
  const isMobile = useIsMobile()
  const [verbData, setVerbData] = useState<Record<string, any>>({})
  const [loading, setLoading]   = useState(true)
  const [drillCards, setDrillCards] = useState<DrillCard[] | null>(null)

  useEffect(() => {
    setLoading(true)
    setDrillCards(null)
    loadVerbData(langCode).then(data => { setVerbData(data); setLoading(false) })
  }, [langCode])

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {drillCards ? (
        <DrillGame
          key={`drill-${langCode}-${drillCards.length}`}
          cards={drillCards}
          isMobile={isMobile}
          onReset={() => setDrillCards(null)}
        />
      ) : (
        <DrillSetup langCode={langCode} verbData={verbData} loading={loading} onStart={c => setDrillCards(c)}/>
      )}
    </div>
  )
}

// ── 2. FLASHCARDS ────────────────────────────────────────────────────────────
function FlashcardGame({ cards, isMobile }: { cards: FlashCard[]; isMobile: boolean }) {
  return (
    <SharedFlashcardGame
      cards={cards.map(c => ({ id: c.id, front: c.front, back: c.back, note: c.note, sub: c.verb }))}
      isMobile={isMobile}
      icon="🃏"
    />
  )
}

function DeckChooser({ language, userDeck, onSelect, onManageDeck }: { language: string; userDeck: FlashCard[]; onSelect: (cards: FlashCard[]) => void; onManageDeck: () => void }) {
  const isMobile = useIsMobile()
  const hp = isMobile ? '16px' : '48px'
  const builtIn = language === 'Portuguese' ? PREBUILT_DECKS_PT : PREBUILT_DECKS_ES

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg, padding: `28px ${hp}` }}>
      <h2 style={{ fontFamily: AF.h, fontSize: 22, fontWeight: 700, color: C.text, margin: '0 0 4px', letterSpacing: '-0.7px' }}>Choose a deck</h2>
      <p style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, margin: '0 0 28px' }}>Select a pre-made set or practice from your own deck.</p>

      <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 12 }}>MY DECK</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderRadius: 10, border: `1px solid ${C.accentBorder}`, background: C.accentBg, marginBottom: 24, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontFamily: AF.h, fontSize: 16, fontWeight: 700, color: 'var(--accent)', letterSpacing: '-0.3px' }}>My Deck</div>
          <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, marginTop: 2 }}>
            {userDeck.length > 0 ? `${userDeck.length} saved cards` : 'No cards saved yet — browse the library to add some'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onManageDeck} style={{ fontFamily: AF.b, fontSize: 12, color: 'var(--accent)', background: 'transparent', border: `1px solid ${C.accentBorder}`, padding: '7px 14px', borderRadius: 6, cursor: 'pointer' }}>
            Browse library
          </button>
          {userDeck.length > 0 && (
            <button onClick={() => onSelect(userDeck)} style={{ fontFamily: AF.b, fontSize: 12, fontWeight: 700, background: 'var(--accent)', color: C.bg, border: 'none', padding: '7px 14px', borderRadius: 6, cursor: 'pointer' }}>
              Practice →
            </button>
          )}
        </div>
      </div>

      <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 12 }}>INCLUDED DECKS</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {builtIn.map(deck => (
          <div key={deck.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.surf, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ fontSize: 28, lineHeight: 1 }}>{deck.emoji}</span>
              <div>
                <div style={{ fontFamily: AF.h, fontSize: 15, fontWeight: 600, color: C.text, letterSpacing: '-0.3px' }}>{deck.name}</div>
                <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, marginTop: 2 }}>{deck.desc}</div>
                <div style={{ fontFamily: AF.m, fontSize: 11, color: C.dim, marginTop: 4 }}>{deck.cards.length} cards</div>
              </div>
            </div>
            <button onClick={() => onSelect(deck.cards)} style={{ fontFamily: AF.b, fontSize: 13, fontWeight: 700, background: 'var(--accent)', color: C.bg, border: 'none', padding: '9px 18px', borderRadius: 7, cursor: 'pointer', flexShrink: 0 }}>
              Start →
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

function DeckManager({ language, deckIds, onToggle, onClose }: { language: string; deckIds: string[]; onToggle: (id: string) => void; onClose: () => void }) {
  const isMobile = useIsMobile()
  const hp = isMobile ? '16px' : '48px'
  const [tab, setTab] = useState<'library' | 'mine'>('library')
  const allLibrary = (language === 'Portuguese' ? PREBUILT_DECKS_PT : PREBUILT_DECKS_ES).flatMap(d => d.cards)
  const deckCards = allLibrary.filter(c => deckIds.includes(c.id))

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.bg }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `16px ${hp}`, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <div>
          <div style={{ fontFamily: AF.h, fontSize: 17, fontWeight: 700, color: C.text, letterSpacing: '-0.4px' }}>Flashcard Library</div>
          <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted }}>{deckIds.length} saved to My Deck</div>
        </div>
        <button onClick={onClose} style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, background: 'transparent', border: `1px solid ${C.border}`, padding: '7px 16px', borderRadius: 7, cursor: 'pointer' }}>
          ← Back
        </button>
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        {([['library', 'All cards'], ['mine', `My Deck (${deckIds.length})`]] as [string, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id as 'library' | 'mine')} style={{
            flex: 1, padding: '12px', fontFamily: AF.b, fontSize: 14, fontWeight: tab === id ? 700 : 400,
            background: 'transparent', border: 'none',
            borderBottom: `2px solid ${tab === id ? 'var(--accent)' : 'transparent'}`,
            color: tab === id ? 'var(--accent)' : C.muted, cursor: 'pointer',
          }}>{label}</button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: `16px ${hp}` }}>
        {tab === 'library' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {allLibrary.map(card => {
              const inDeck = deckIds.includes(card.id)
              return (
                <div key={card.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontFamily: AF.m, fontSize: 14, color: C.text, fontWeight: 500 }}>{card.front}</span>
                    <span style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, marginLeft: 10 }}>{card.back}</span>
                  </div>
                  <span style={{ fontFamily: AF.m, fontSize: 10, color: C.dim, flexShrink: 0 }}>{card.note}</span>
                  <button onClick={() => onToggle(card.id)} style={{
                    fontFamily: AF.b, fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 6, border: 'none',
                    background: inDeck ? C.greenBg : C.accentBg,
                    color: inDeck ? 'var(--success)' : 'var(--accent)',
                    cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
                  }}>{inDeck ? '✓ Saved' : '+ Add'}</button>
                </div>
              )
            })}
          </div>
        )}
        {tab === 'mine' && (
          deckCards.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px' }}>
              <div style={{ fontSize: 36, marginBottom: 16 }}>📭</div>
              <div style={{ fontFamily: AF.h, fontSize: 18, color: C.muted, marginBottom: 8 }}>No cards yet</div>
              <div style={{ fontFamily: AF.b, fontSize: 14, color: C.dim }}>Switch to "All cards" to add some.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {deckCards.map(card => (
                <div key={card.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontFamily: AF.m, fontSize: 14, color: C.text, fontWeight: 500 }}>{card.front}</span>
                    <span style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, marginLeft: 10 }}>{card.back}</span>
                  </div>
                  <span style={{ fontFamily: AF.m, fontSize: 10, color: C.dim, flexShrink: 0 }}>{card.note}</span>
                  <button onClick={() => onToggle(card.id)} style={{ fontFamily: AF.b, fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 6, border: 'none', background: C.redBg, color: 'var(--error)', cursor: 'pointer', flexShrink: 0 }}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  )
}

function FlashcardsSection({ language }: { language: string }) {
  const [deckIds, setDeckIds] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('c8-deck') || '[]') } catch { return [] }
  })
  const [view, setView] = useState<'chooser' | 'manager' | 'game'>('chooser')
  const [activeCards, setActiveCards] = useState<FlashCard[]>([])
  const isMobile = useIsMobile()

  useEffect(() => {
    try { localStorage.setItem('c8-deck', JSON.stringify(deckIds)) } catch { /* ignore */ }
  }, [deckIds])

  const allLibrary = (language === 'Portuguese' ? PREBUILT_DECKS_PT : PREBUILT_DECKS_ES).flatMap(d => d.cards)
  const userDeck = useMemo(() => allLibrary.filter(c => deckIds.includes(c.id)), [deckIds, language])

  const toggleDeckCard = (id: string) => {
    setDeckIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  if (view === 'game') return (
    <FlashcardGame key={`flash-${language}-${activeCards.length}`} cards={activeCards} isMobile={isMobile}/>
  )
  if (view === 'manager') return (
    <DeckManager language={language} deckIds={deckIds} onToggle={toggleDeckCard} onClose={() => setView('chooser')}/>
  )
  return (
    <DeckChooser
      language={language}
      userDeck={userDeck}
      onSelect={cards => { setActiveCards(cards); setView('game') }}
      onManageDeck={() => setView('manager')}
    />
  )
}

// ── 3. SENTENCES ─────────────────────────────────────────────────────────────
function SentencesSection({ language }: { language: string }) {
  const isMobile = useIsMobile()
  const cards = SENTENCE_CARDS

  const [idx, setIdx]       = useState(0)
  const [placed, setPlaced] = useState<number[]>([])
  const [checked, setChecked] = useState(false)
  const [score, setScore]   = useState(0)
  const [done, setDone]     = useState(false)
  const card = cards[idx]

  const isCorrect = () => placed.length === card.answer.length &&
    placed.every((ti, i) => card.tiles[ti] === card.answer[i])

  const advance = useCallback(() => {
    if (checked && isCorrect()) setScore(s => s + 1)
    if (idx + 1 >= cards.length) { setDone(true); return }
    setIdx(i => i + 1); setPlaced([]); setChecked(false)
  }, [idx, cards.length, checked, placed])

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !checked && placed.length > 0) setChecked(true)
      if (e.key === 'Enter' && checked) advance()
      if (e.key === 'Backspace' && !checked) { e.preventDefault(); setPlaced(p => p.slice(0, -1)) }
      if (e.key === 'Escape' && !checked) setPlaced([])
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [checked, placed, advance])

  if (done) return (
    <ScoreScreen score={score} total={cards.length}
      onRetry={() => { setIdx(0); setPlaced([]); setChecked(false); setScore(0); setDone(false) }}/>
  )

  const correct = checked && isCorrect()
  const wrong   = checked && !isCorrect()
  const hp = isMobile ? '16px' : '48px'

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: correct ? C.greenBg : wrong ? C.redBg : C.bg, transition: 'background 0.25s' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: `12px ${hp} 0` }}>
        <div style={{ flex: 1, height: 3, background: C.border, borderRadius: 2 }}>
          <div style={{ height: '100%', background: 'var(--accent)', width: `${(idx / cards.length) * 100}%`, transition: 'width 0.4s', borderRadius: 2 }}/>
        </div>
        <CounterBadge current={idx + 1} total={cards.length}/>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: `20px ${hp}`, overflow: 'auto' }}>
        <ModeInstruction icon="🔤" text="Tap words to build the translation. Tap a placed word to remove it."/>

        <div style={{ fontFamily: AF.h, fontSize: isMobile ? 18 : 22, fontWeight: 600, color: C.text, marginBottom: 20, letterSpacing: '-0.4px', lineHeight: 1.35 }}>
          {card.prompt}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ fontFamily: AF.m, fontSize: 9, color: 'var(--accent)', letterSpacing: '0.14em', fontWeight: 600 }}>YOUR ANSWER</span>
            <div style={{ flex: 1, height: 1, background: C.border }}/>
            {placed.length > 0 && !checked && (
              <button onClick={() => setPlaced([])} style={{ fontFamily: AF.b, fontSize: 11, color: C.muted, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', gap: 4, alignItems: 'center' }}>
                Clear <Kbd>Esc</Kbd>
              </button>
            )}
          </div>
          <div style={{
            minHeight: 60, borderRadius: 10, padding: '12px 14px',
            border: `2px dashed ${checked ? (correct ? C.greenBorder : C.redBorder) : placed.length > 0 ? C.accentBorder : C.border}`,
            background: checked ? (correct ? C.greenBg : C.redBg) : placed.length > 0 ? C.accentBg : 'transparent',
            display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', transition: 'all 0.2s',
          }}>
            {placed.length === 0
              ? <span style={{ fontFamily: AF.b, fontSize: 14, color: C.dim }}>↑ Tap words below to place them here</span>
              : placed.map((ti, i) => (
                  <button key={i} onClick={() => !checked && setPlaced(p => p.filter((_, j) => j !== i))}
                    className="c8-tile-btn"
                    style={{
                      fontFamily: AF.m, fontSize: 15, color: C.text, fontWeight: 500,
                      background: C.surf, border: `1px solid ${C.accentBorder}`,
                      padding: '8px 16px', borderRadius: 7, cursor: checked ? 'default' : 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}>
                    {card.tiles[ti]}
                  </button>
                ))
            }
          </div>
        </div>

        {wrong && (
          <div className="c8-pop" style={{ borderLeft: `3px solid ${C.redBorder}`, paddingLeft: 14, marginBottom: 16 }}>
            <div style={{ fontFamily: AF.m, fontSize: 11, color: 'var(--error)', letterSpacing: '0.08em', marginBottom: 4 }}>CORRECT ORDER</div>
            <div style={{ fontFamily: AF.m, fontSize: 15, color: C.text }}>{card.correct}</div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
          {!checked ? (
            <>
              <button onClick={() => placed.length > 0 && setChecked(true)} disabled={placed.length === 0} style={{ fontFamily: AF.b, fontSize: 14, fontWeight: 700, background: placed.length > 0 ? 'var(--accent)' : C.surf2, color: placed.length > 0 ? C.bg : C.dim, border: 'none', padding: '11px 22px', borderRadius: 8, cursor: placed.length > 0 ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 8 }}>
                Check <Kbd>↵</Kbd>
              </button>
              <button onClick={() => setPlaced(p => p.slice(0, -1))} style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, background: 'transparent', border: `1px solid ${C.border}`, padding: '11px 16px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                Undo last <Kbd>⌫</Kbd>
              </button>
            </>
          ) : (
            <button onClick={advance} style={{ fontFamily: AF.b, fontSize: 14, fontWeight: 700, background: correct ? 'var(--success)' : C.surf2, color: correct ? C.bg : C.text, border: `1px solid ${correct ? C.greenBorder : C.border}`, padding: '11px 24px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              {correct ? '✓ Correct! Next →' : 'Continue →'} <Kbd>↵</Kbd>
            </button>
          )}
        </div>

        <div style={{ marginTop: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <span style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', fontWeight: 600 }}>WORD BANK</span>
            <div style={{ flex: 1, height: 1, background: C.border }}/>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
            {card.tiles.map((tile, i) => {
              const isPlaced = placed.includes(i)
              return (
                <button key={i} onClick={() => !isPlaced && !checked && setPlaced(p => [...p, i])}
                  className="c8-tile-btn"
                  style={{
                    fontFamily: AF.m, fontSize: 15, color: isPlaced ? C.dim : C.text,
                    background: isPlaced ? 'transparent' : C.surf,
                    border: `1px solid ${isPlaced ? C.border : C.border2}`,
                    padding: '10px 18px', borderRadius: 8, fontWeight: 500,
                    cursor: isPlaced || checked ? 'default' : 'pointer',
                    opacity: isPlaced ? 0.2 : 1,
                  }}>
                  {tile}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── 4. QUIZ ──────────────────────────────────────────────────────────────────
function generateOptions(card: DrillCard, allCards: DrillCard[]): string[] {
  const correct = card.answer
  const others = [...new Set(allCards.map(c => c.answer).filter(a => a !== correct))]
  const distractors = others.sort(() => Math.random() - 0.5).slice(0, 3)
  return [...distractors, correct].sort(() => Math.random() - 0.5)
}

function QuizSection({ langCode }: { langCode: string }) {
  const isMobile = useIsMobile()
  const [cards, setCards]   = useState<DrillCard[]>([])
  const [quizLoading, setQuizLoading] = useState(true)

  useEffect(() => {
    setQuizLoading(true)
    loadVerbData(langCode).then(data => {
      const pronouns = langCode === 'pt' ? PRONOUNS_PT : langCode === 'fi' ? PRONOUNS_FI : PRONOUNS_ES
      const generated = generateDrillCards({ data, langCode, pronouns, tenses: ['present'], verbType: 'all', count: 10 })
      setCards(generated)
      setQuizLoading(false)
    })
  }, [langCode])

  const [idx, setIdx]       = useState(0)
  const [opts, setOpts]     = useState<string[]>([])
  const [chosen, setChosen] = useState<string | null>(null)
  const [score, setScore]   = useState(0)
  const [done, setDone]     = useState(false)

  useEffect(() => {
    if (cards.length > 0) { setIdx(0); setChosen(null); setScore(0); setDone(false); setOpts(generateOptions(cards[0], cards)) }
  }, [cards])

  const card = cards[idx]

  const choose = useCallback((opt: string) => {
    if (chosen !== null || !card) return
    setChosen(opt)
    const isRight = opt === card.answer
    recordAttempt({ timestamp: Date.now(), infinitive: card.verb, tense: card.tense, pronoun: card.pronoun, status: isRight ? 'correct' : 'incorrect' })
    if (isRight) setScore(s => s + 1)
  }, [chosen, card])

  const advance = useCallback(() => {
    const next = idx + 1
    if (next >= cards.length) {
      if (cards.length > 0) saveSession({ verb: cards[0].verb, tense: cards[0].tenseLabel, correct: score, total: cards.length, timestamp: Date.now() })
      setDone(true); return
    }
    setIdx(next); setChosen(null)
    setOpts(generateOptions(cards[next], cards))
  }, [idx, cards, score])

  const retryQuiz = useCallback(() => {
    loadVerbData(langCode).then(data => {
      const pronouns = langCode === 'pt' ? PRONOUNS_PT : langCode === 'fi' ? PRONOUNS_FI : PRONOUNS_ES
      const newCards = generateDrillCards({ data, langCode, pronouns, tenses: ['present'], verbType: 'all', count: 10 })
      setCards(newCards)
      setScore(0); setDone(false)
    })
  }, [langCode])

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return
      const ki = ['1','2','3','4'].indexOf(e.key)
      if (ki >= 0 && ki < opts.length) choose(opts[ki])
      if ((e.key === 'Enter' || e.key === ' ') && chosen !== null) { e.preventDefault(); advance() }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [opts, choose, chosen, advance])

  if (quizLoading || !card) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <span style={{ fontFamily: AF.b, fontSize: 14, color: C.muted }}>Loading…</span>
    </div>
  )

  if (done) return (
    <ScoreScreen score={score} total={cards.length} onRetry={retryQuiz}/>
  )

  const hp = isMobile ? '20px' : '64px'
  const LABELS = ['1', '2', '3', '4']

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.bg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: `12px ${hp} 0` }}>
        <div style={{ flex: 1, height: 3, background: C.border, borderRadius: 2 }}>
          <div style={{ height: '100%', background: 'var(--accent)', width: `${(idx / cards.length) * 100}%`, transition: 'width 0.4s', borderRadius: 2 }}/>
        </div>
        <CounterBadge current={idx + 1} total={cards.length}/>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: `20px ${hp}`, maxWidth: 560, alignSelf: 'center', width: '100%' }}>
        <ModeInstruction icon="🎯" text="Choose the correct conjugation — no typing needed"/>

        <div style={{ alignSelf: 'flex-start', width: '100%', marginBottom: 28 }}>
          <div style={{ fontFamily: AF.h, fontSize: isMobile ? 48 : 72, fontWeight: 700, color: C.text, letterSpacing: '-3px', lineHeight: 1, marginBottom: 10 }}>
            {card.verb}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: AF.b, fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>{card.tenseLabel}</span>
            <span style={{ color: C.border }}>·</span>
            <span style={{ fontFamily: AF.b, fontSize: 12, color: C.muted }}>{card.verbType}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: AF.m, fontSize: 11, color: C.dim, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Conjugate for</span>
            <span style={{ fontFamily: AF.m, fontSize: isMobile ? 22 : 28, fontWeight: 500, color: C.muted, letterSpacing: '-0.5px' }}>{card.pronoun}</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, width: '100%', marginBottom: 20 }}>
          {opts.map((opt, i) => {
            const isChosen  = chosen === opt
            const isOptCorrect = opt === card.answer
            const showRight = chosen !== null && isOptCorrect
            const showWrong = isChosen && !isOptCorrect

            let bg     = C.surf
            let border = C.border2
            let col    = C.text
            if (showRight) { bg = C.greenBg;   border = C.greenBorder; col = 'var(--success)' }
            if (showWrong) { bg = C.redBg;     border = C.redBorder;   col = 'var(--error)' }

            return (
              <button key={opt} onClick={() => choose(opt)} style={{
                fontFamily: AF.m, fontSize: isMobile ? 20 : 26, fontWeight: 500,
                color: col, background: bg, border: `2px solid ${border}`,
                padding: `${isMobile ? 16 : 20}px 16px`, borderRadius: 11,
                cursor: chosen !== null ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 12,
                letterSpacing: '-0.5px', transition: 'background 0.2s, border-color 0.2s, color 0.2s',
                textAlign: 'left',
              }}>
                <span style={{
                  fontFamily: AF.b, fontSize: 11, letterSpacing: '0.1em', fontWeight: 700,
                  color: showRight ? 'var(--success)' : showWrong ? 'var(--error)' : C.dim,
                  display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
                }}>
                  {showRight ? '✓' : showWrong ? '✗' : LABELS[i]}
                  <Kbd>{LABELS[i]}</Kbd>
                </span>
                {opt}
              </button>
            )
          })}
        </div>

        {chosen !== null && (
          <div className="c8-pop" style={{ width: '100%' }}>
            <button onClick={advance} style={{
              fontFamily: AF.b, fontSize: 14, fontWeight: 700,
              background: chosen === card.answer ? 'var(--success)' : C.surf,
              color: chosen === card.answer ? C.bg : C.text,
              border: `1px solid ${chosen === card.answer ? C.greenBorder : C.border}`,
              padding: '12px 24px', borderRadius: 8, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {chosen === card.answer ? '✓ Correct! Next →' : 'Continue →'} <Kbd>↵</Kbd>
            </button>
          </div>
        )}
      </div>

      <KbdRow hints={chosen === null
        ? [['1', 'option 1'], ['2', 'option 2'], ['3', 'option 3'], ['4', 'option 4']]
        : [['↵ / Space', 'next card']]
      }/>
    </div>
  )
}

// ── Mode tab bar ─────────────────────────────────────────────────────────────
type Mode = 'drills' | 'flashcards' | 'sentences' | 'quiz'

function DrillsIcon({ active }: { active: boolean }) {
  const s = active ? 'var(--accent)' : 'var(--muted)'
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
      <path d="M2 11L8.5 3L11.5 6.5L5 14H2V11Z" stroke={s} strokeWidth="1.35" strokeLinejoin="round"/>
      <path d="M8.5 3L10.5 1.5C11.2 0.9 12.2 1 12.8 1.7L13.3 2.3C14 3 13.9 4 13.2 4.6L11.5 6.5" stroke={s} strokeWidth="1.35" strokeLinejoin="round"/>
      <path d="M2 14.5H12" stroke={s} strokeWidth="1" strokeLinecap="round" opacity="0.35"/>
    </svg>
  )
}

function FlashcardsIcon({ active }: { active: boolean }) {
  const s = active ? 'var(--accent)' : 'var(--muted)'
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
      <rect x="2.5" y="4.5" width="10" height="7.5" rx="1.4" stroke={s} strokeWidth="1.35"/>
      <path d="M4.5 4.5V3.5C4.5 2.95 4.95 2.5 5.5 2.5H12.5C13.05 2.5 13.5 2.95 13.5 3.5V9.5C13.5 10.05 13.05 10.5 12.5 10.5H12.5" stroke={s} strokeWidth="1.1" opacity="0.5"/>
      <path d="M5.2 9L7 10.8L10 7" stroke={s} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function SentencesIcon({ active }: { active: boolean }) {
  const s = active ? 'var(--accent)' : 'var(--muted)'
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
      <rect x="1" y="2" width="5.5" height="4" rx="1.1" stroke={s} strokeWidth="1.35"/>
      <rect x="8.5" y="2" width="5.5" height="4" rx="1.1" stroke={s} strokeWidth="1.35"/>
      <rect x="4" y="9" width="7" height="4" rx="1.1" stroke={s} strokeWidth="1.35"/>
      <path d="M3.75 6.5C3.75 7.6 4.6 8.5 5.75 9" stroke={s} strokeWidth="1" strokeLinecap="round" opacity="0.5"/>
      <path d="M11.25 6.5C11.25 7.6 10.4 8.5 9.25 9" stroke={s} strokeWidth="1" strokeLinecap="round" opacity="0.5"/>
    </svg>
  )
}

function QuizIcon({ active }: { active: boolean }) {
  const s = active ? 'var(--accent)' : 'var(--muted)'
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
      <rect x="1" y="1.5" width="5.5" height="5" rx="1.2" stroke={s} strokeWidth="1.35"/>
      <rect x="8.5" y="1.5" width="5.5" height="5" rx="1.2" stroke={s} strokeWidth="1.35"/>
      <rect x="1" y="8.5" width="5.5" height="5" rx="1.2" stroke={s} strokeWidth="1.35"/>
      <rect x="8.5" y="8.5" width="5.5" height="5" rx="1.2" stroke={s} strokeWidth="1.35" opacity="0.4"/>
      <path d="M2.8 4.2L3.8 5.2L5.8 3" stroke={s} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

const MODE_ICONS: Record<Mode, (active: boolean) => React.ReactNode> = {
  drills:     (a) => <DrillsIcon active={a}/>,
  flashcards: (a) => <FlashcardsIcon active={a}/>,
  sentences:  (a) => <SentencesIcon active={a}/>,
  quiz:       (a) => <QuizIcon active={a}/>,
}

const MODES: { id: Mode; label: string }[] = [
  { id: 'drills',     label: 'Drills'     },
  { id: 'flashcards', label: 'Flashcards' },
  { id: 'sentences',  label: 'Sentences'  },
  { id: 'quiz',       label: 'Quiz'       },
]

function ModeBar({ mode, onMode }: { mode: Mode; onMode: (m: Mode) => void }) {
  const isMobile = useIsMobile()
  return (
    <div style={{
      display: 'flex', flexShrink: 0,
      background: C.surf,
      borderBottom: `1px solid ${C.border}`,
      padding: '0 8px',
    }}>
      {MODES.map(m => {
        const active = mode === m.id
        return (
          <button key={m.id} onClick={() => onMode(m.id)} style={{
            fontFamily: AF.b,
            fontSize: isMobile ? 12.5 : 14,
            fontWeight: active ? 600 : 400,
            padding: isMobile ? '12px 13px' : '14px 22px',
            border: 'none',
            borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
            background: 'transparent',
            color: active ? 'var(--accent)' : C.muted,
            cursor: 'pointer',
            transition: 'color 0.15s, border-color 0.15s',
            display: 'flex', alignItems: 'center',
            gap: isMobile ? 5 : 6,
            whiteSpace: 'nowrap',
            letterSpacing: active ? '-0.1px' : '0',
          }}>
            {MODE_ICONS[m.id](active)}
            {m.label}
          </button>
        )
      })}
    </div>
  )
}

const MODE_META: Record<Mode, { icon: string; title: string; desc: string }> = {
  drills:     { icon: '✏️', title: 'Drills',      desc: 'Type the correct conjugation from memory.' },
  flashcards: { icon: '🃏', title: 'Flashcards',  desc: 'Flip cards and rate how well you remembered.' },
  sentences:  { icon: '📝', title: 'Sentences',   desc: 'Build sentences word by word.' },
  quiz:       { icon: '⚡', title: 'Quick Quiz',  desc: 'Multiple-choice conjugation practice.' },
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Page() {
  const pageContext = usePageContext()
  const langCode = (pageContext.routeParams as { language?: string } | undefined)?.language ?? 'es'
  const languageName = langCodeToName(langCode)
  const isMobile = useIsMobile()
  const hp = isMobile ? '16px' : '48px'
  const [soundOn, setSoundOn] = useState(true)
  useEffect(() => { setSoundOn(sound.isEnabled()) }, [])

  const VALID_MODES: Mode[] = ['drills', 'flashcards', 'sentences', 'quiz']
  const hashToMode = (): Mode => {
    if (typeof window === 'undefined') return 'drills'
    const h = window.location.hash.slice(1) as Mode
    return VALID_MODES.includes(h) ? h : 'drills'
  }
  const [mode, setMode] = useState<Mode>(hashToMode)

  useEffect(() => {
    setMode(hashToMode())
    const onHash = () => setMode(hashToMode())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const handleMode = (m: Mode) => {
    window.location.hash = m
    setMode(m)
  }

  const meta = MODE_META[mode]

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg }}>
      {/* MotifBand header */}
      <div style={{ position: 'relative', overflow: 'hidden', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.55,
          WebkitMaskImage: 'linear-gradient(to left, #000, transparent 60%)',
          maskImage: 'linear-gradient(to left, #000, transparent 60%)',
        }}>
          <MotifBand language={languageName} height={isMobile ? 96 : 112} opacity={0.7}/>
        </div>
        <div style={{ position: 'relative', padding: `22px ${hp} 18px` }}>
          <button
            onClick={() => { const v = !soundOn; sound.setEnabled(v); setSoundOn(v) }}
            title={soundOn ? 'Mute sounds' : 'Unmute sounds'}
            style={{ position: 'absolute', top: 18, right: hp, fontFamily: AF.b, fontSize: 13, color: C.dim, background: 'transparent', border: `1px solid ${C.border}`, padding: '5px 9px', borderRadius: 6, cursor: 'pointer' }}
          >{soundOn ? '🔊' : '🔇'}</button>
          <div style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8 }}>
            {motifLabel(languageName)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>{meta.icon}</span>
            <h1 style={{ fontFamily: AF.h, fontSize: isMobile ? 22 : 26, fontWeight: 700, color: C.text, margin: 0, letterSpacing: '-0.7px' }}>
              {meta.title}
            </h1>
          </div>
          <p style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, margin: 0 }}>{meta.desc}</p>
          <a href={`/learn-${({ es: 'spanish', pt: 'portuguese', fi: 'finnish' } as Record<string, string>)[langCode] ?? 'spanish'}/garden`} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 13,
            fontFamily: AF.b, fontSize: 12, fontWeight: 700, color: 'var(--accent)',
            background: 'var(--accent-bg)', border: '1px solid var(--accent-border)',
            padding: '7px 13px', borderRadius: 8, textDecoration: 'none',
          }}>🌿 Training for the Conjugarden — take these skills on an expedition →</a>
        </div>
      </div>

      {/* Mobile-only mode switcher */}
      {isMobile && (
        <div style={{ display: 'flex', gap: 6, padding: '10px 16px', overflowX: 'auto', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          {MODES.map(m => (
            <button key={m.id} onClick={() => handleMode(m.id)} style={{
              fontFamily: AF.b, fontSize: 12, fontWeight: mode === m.id ? 600 : 400, flexShrink: 0,
              padding: '6px 14px', borderRadius: 20,
              border: `1px solid ${mode === m.id ? 'var(--accent)' : C.border}`,
              background: mode === m.id ? 'var(--accent)' : 'transparent',
              color: mode === m.id ? C.bg : C.muted, cursor: 'pointer',
            }}>
              {m.label}
            </button>
          ))}
        </div>
      )}

      {/* Exercise content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {mode === 'drills'     && <DrillsSection     key={`drills-${langCode}`}     langCode={langCode}/>}
        {mode === 'flashcards' && <FlashcardsSection key={`flash-${langCode}`}      language={languageName}/>}
        {mode === 'sentences'  && <SentencesSection  key={`sent-${langCode}`}       language={languageName}/>}
        {mode === 'quiz'       && <QuizSection       key={`quiz-${langCode}`}        langCode={langCode}/>}
      </div>
    </div>
  )
}
